import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import LennonLogo from "@/components/LennonLogo";
import { COMPANY } from "@/data/content";

const LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/creators", label: "Creators" },
  { to: "/work", label: "Work" },
  { to: "/about", label: "About" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-md bg-paper/85 border-b border-line" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-10 lg:px-14 flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="group transition-opacity hover:opacity-70">
          <LennonLogo variant="horizontal" size="md" />
        </Link>

        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `nav-link font-sans text-sm text-ink-800 hover:text-ink transition-colors ${isActive ? "active text-ink" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={COMPANY.companiesUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-for-companies"
            className="hidden lg:inline-flex items-center gap-1.5 text-xs text-warm-600 hover:text-ink transition-colors uppercase tracking-widest"
          >
            Lennon Global <ArrowUpRight size={12} />
          </a>
          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="ml-2 inline-flex items-center gap-2 bg-ink text-paper px-5 py-2.5 text-sm font-medium hover:bg-ink-800 transition-colors"
          >
            Contact
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 text-ink"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden fixed inset-0 top-16 bg-paper z-40">
          <div className="flex flex-col px-6 py-10 gap-6">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `display text-4xl ${isActive ? "text-ink" : "text-ink-800"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-contact"
              className="display text-4xl text-ink"
            >
              Contact →
            </Link>
            <a
              href={COMPANY.companiesUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="mobile-nav-for-companies"
              className="mt-8 inline-flex items-center gap-2 eyebrow text-warm-600"
            >
              Lennon Global · lennon.global <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
